import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { backofficeApi } from '@/lib/backoffice-api'
import type {
  Paginated,
  BackofficeTenantListItem,
  BackofficeTenantDetail,
  CreateTenantResult,
} from '@/types/backoffice'

export interface ListTenantsParams {
  search?: string
  active?: boolean
  page?: number
  perPage?: number
}

export interface CreateTenantInput {
  name: string
  cnpj: string
  primaryColor?: string
  adminName: string
  adminEmail: string
}

export interface UpdateTenantInput {
  name: string
  cnpj: string
  primaryColor?: string
}

const KEYS = {
  list: (p: ListTenantsParams) => ['backoffice', 'tenants', p] as const,
  detail: (id: string) => ['backoffice', 'tenants', id] as const,
}

export function useBackofficeTenants(params: ListTenantsParams = {}) {
  return useQuery({
    queryKey: KEYS.list(params),
    queryFn: () =>
      backofficeApi
        .get<Paginated<BackofficeTenantListItem>>('/backoffice/tenants', { params })
        .then((r) => r.data),
  })
}

export function useBackofficeTenant(id: string) {
  return useQuery({
    queryKey: KEYS.detail(id),
    queryFn: () =>
      backofficeApi.get<BackofficeTenantDetail>(`/backoffice/tenants/${id}`).then((r) => r.data),
    enabled: !!id,
  })
}

export function useCreateBackofficeTenant() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: CreateTenantInput) =>
      backofficeApi.post<CreateTenantResult>('/backoffice/tenants', data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['backoffice', 'tenants'] }),
  })
}

export function useUpdateBackofficeTenant(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: UpdateTenantInput) =>
      backofficeApi
        .put<BackofficeTenantListItem>(`/backoffice/tenants/${id}`, data)
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['backoffice', 'tenants'] }),
  })
}

export function useSetTenantActive() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      backofficeApi
        .patch<BackofficeTenantListItem>(`/backoffice/tenants/${id}/${active ? 'activate' : 'deactivate'}`)
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['backoffice', 'tenants'] }),
  })
}
